import React, { useLayoutEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { tokens } from "../theme";

/**
 * CRA / webpack-dev-server hata overlay'ini gizler; kullanıcı sadece ErrorBoundary fallback görsün.
 */
function hideDevErrorOverlay() {
  if (typeof document === "undefined") return;
  const selectors = [
    "#webpack-dev-server-client-overlay",
    "#react-error-overlay",
    ".react-error-overlay",
    "[data-react-error-overlay]",
    "iframe[style*='position: fixed'][style*='inset: 0']",
  ];
  selectors.forEach((sel) => {
    try {
      const el = document.querySelector(sel);
      if (el) {
        el.style.setProperty("display", "none", "important");
        el.setAttribute("aria-hidden", "true");
      }
    } catch (_) {}
  });
  const bodyChildren = document.body?.children;
  if (bodyChildren) {
    for (let i = 0; i < bodyChildren.length; i++) {
      const node = bodyChildren[i];
      if (node.id === "root") continue;
      if (node.tagName === "SCRIPT" || node.tagName === "NOSCRIPT") continue;
      const style = node.getAttribute?.("style") || "";
      if (
        node.tagName === "IFRAME" ||
        (style.includes("position: fixed") && style.includes("z-index"))
      ) {
        node.style.setProperty("display", "none", "important");
      }
    }
  }
}

/**
 * React Error Boundary: Render sırasında oluşan hataları yakalar,
 * kullanıcıya teknik detay yerine anlaşılır bir mesaj gösterir.
 * Dev overlay gösterilmez; sadece bu fallback görünür.
 */
class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    hideDevErrorOverlay();
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught:", error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <this.props.Fallback
          error={this.state.error}
          onRetry={() => this.setState({ hasError: false, error: null })}
        />
      );
    }
    return this.props.children;
  }
}

function ErrorFallback({ error, onRetry }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation("common");

  useLayoutEffect(() => {
    hideDevErrorOverlay();
    const t1 = requestAnimationFrame(() => hideDevErrorOverlay());
    const t2 = setTimeout(hideDevErrorOverlay, 0);
    const t3 = setTimeout(hideDevErrorOverlay, 100);
    return () => {
      cancelAnimationFrame(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[700]} 100%)`
            : `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.grey[200]} 100%)`,
      }}
    >
      <Box
        sx={{
          maxWidth: 440,
          width: "100%",
          textAlign: "center",
          p: 4,
          borderRadius: 3,
          boxShadow: 4,
          backgroundColor:
            theme.palette.mode === "dark"
              ? colors.primary[600]
              : colors.grey[50],
          border: `1px solid ${colors.grey[700]}40`,
        }}
      >
        <ErrorOutlineOutlinedIcon
          sx={{
            fontSize: 64,
            color: colors.redAccent[500],
            mb: 2,
          }}
        />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: colors.grey[100],
            mb: 1,
          }}
        >
          {t("errorBoundaryTitle")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: colors.grey[300],
            mb: 3,
          }}
        >
          {t("errorBoundaryMessage")}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<RefreshOutlinedIcon />}
            onClick={handleReload}
            sx={{
              background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
              color: colors.grey[100],
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {t("errorBoundaryReload")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<HomeOutlinedIcon />}
            onClick={handleGoHome}
            sx={{
              borderColor: colors.grey[500],
              color: colors.grey[200],
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {t("errorBoundaryGoHome")}
          </Button>
        </Box>
        {onRetry && (
          <Button
            variant="text"
            size="small"
            onClick={onRetry}
            sx={{
              mt: 2,
              color: colors.grey[400],
              textTransform: "none",
            }}
          >
            {t("errorBoundaryTryAgain")}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default function ErrorBoundary({ children }) {
  return (
    <ErrorBoundaryClass Fallback={ErrorFallback}>
      {children}
    </ErrorBoundaryClass>
  );
}
